import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";

// Capture OAuth hash SYNCHRONOUSLY at module load — before any component effect
// runs, so AuthCallback's hash-clearing replaceState can't beat us.
const HAS_OAUTH_HASH =
    typeof window !== "undefined" &&
    typeof window.location?.hash === "string" &&
    window.location.hash.includes("session_id=");

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(undefined); // undefined = checking
    const [orgs, setOrgs] = useState([]);
    const [memberships, setMemberships] = useState([]);
    const [currentOrg, setCurrentOrg] = useState(null);
    const [features, setFeatures] = useState({});
    const [loading, setLoading] = useState(true);

    const refreshOrgs = useCallback(async () => {
        try {
            const { data } = await api.get("/orgs");
            setOrgs(data.orgs || []);
            setMemberships(data.memberships || []);
            const stored = localStorage.getItem("bl_org_id");
            const pick =
                (stored && data.orgs.find((o) => o.id === stored)) || data.orgs[0] || null;
            if (pick) {
                localStorage.setItem("bl_org_id", pick.id);
                setCurrentOrg(pick);
                try {
                    const { data: detail } = await api.get(`/orgs/${pick.id}`);
                    setFeatures(detail.features || {});
                    setCurrentOrg(detail.org);
                } catch {}
            } else {
                setCurrentOrg(null);
            }
        } catch (e) {
            // ignore
        }
    }, []);

    const bootstrap = useCallback(async () => {
        // If returning from Google OAuth, AuthCallback will handle it; skip /me
        if (HAS_OAUTH_HASH) {
            setLoading(false);
            setUser(null);
            return;
        }
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
            await refreshOrgs();
        } catch {
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, [refreshOrgs]);

    useEffect(() => {
        bootstrap();
    }, [bootstrap]);

    const login = async (email, password) => {
        try {
            const { data } = await api.post("/auth/login", { email, password });
            // If MFA step is required, don't set user yet.
            if (data?.mfa_required) {
                return {
                    ok: true,
                    mfaRequired: true,
                    challengeToken: data.challenge_token,
                    expiresInMinutes: data.expires_in_minutes,
                    emailHint: data.email_hint,
                };
            }
            setUser(data.user);
            await refreshOrgs();
            return { ok: true };
        } catch (e) {
            return { ok: false, error: formatApiError(e) };
        }
    };

    const loginVerify = async ({ challengeToken, code, rememberDevice }) => {
        try {
            const { data } = await api.post("/auth/login/verify", {
                challenge_token: challengeToken,
                code,
                remember_device: !!rememberDevice,
            });
            setUser(data.user);
            await refreshOrgs();
            return { ok: true };
        } catch (e) {
            return { ok: false, error: formatApiError(e) };
        }
    };

    const register = async (name, email, password, confirmPassword) => {
        try {
            const { data } = await api.post("/auth/register", {
                name, email, password, confirm_password: confirmPassword,
            });
            setUser(data.user);
            return { ok: true, verificationSent: !!data.verification_sent };
        } catch (e) {
            return { ok: false, error: formatApiError(e) };
        }
    };

    const resendVerification = async (email) => {
        try {
            await api.post("/auth/resend-verification", { email });
            return { ok: true };
        } catch (e) {
            return { ok: false, error: formatApiError(e) };
        }
    };

    const verifyEmail = async (token) => {
        try {
            await api.post("/auth/verify-email", { token });
            // Refresh user to pick up email_verified=true
            try {
                const { data } = await api.get("/auth/me");
                setUser(data);
            } catch {}
            return { ok: true };
        } catch (e) {
            return { ok: false, error: formatApiError(e) };
        }
    };

    const logout = async () => {
        try { await api.post("/auth/logout"); } catch {}
        setUser(null);
        setOrgs([]); setMemberships([]); setCurrentOrg(null); setFeatures({});
        localStorage.removeItem("bl_org_id");
    };

    const switchOrg = async (orgId) => {
        localStorage.setItem("bl_org_id", orgId);
        const o = orgs.find((x) => x.id === orgId);
        setCurrentOrg(o || null);
        try {
            const { data } = await api.get(`/orgs/${orgId}`);
            setFeatures(data.features || {});
            setCurrentOrg(data.org);
        } catch {}
    };

    return (
        <AuthContext.Provider
            value={{
                user, orgs, memberships, currentOrg, features, loading,
                login, loginVerify, register, resendVerification, verifyEmail,
                logout, switchOrg, refreshOrgs, setCurrentOrg,
                setUser,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}
